import type { z } from 'zod';
import { apiErrorSchema, CSRF_HEADER, csrfResponseSchema } from '@ailab/contracts';

// Cliente de la API para la web: credenciales de la misma web, token CSRF de doble envío
// en las mutaciones y errores con el formato uniforme (RF-07.02, RF-07.05).

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

const CSRF_COOKIE = '__Host-ailab_csrf';

function readCsrfCookie(): string | undefined {
  return document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${CSRF_COOKIE}=`))
    ?.slice(CSRF_COOKIE.length + 1);
}

let csrfToken: string | undefined;

/** Guarda el token que devuelve el servidor al iniciar o cerrar sesión. */
export function rememberCsrf(token: string): void {
  csrfToken = token;
}

async function ensureCsrf(fetcher: typeof fetch): Promise<string> {
  const fromCookie = readCsrfCookie();
  if (fromCookie) return fromCookie;
  if (csrfToken) return csrfToken;
  const response = await fetcher('/api/v1/auth/csrf', { credentials: 'same-origin' });
  csrfToken = csrfResponseSchema.parse(await response.json()).csrfToken;
  return csrfToken;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  fetcher?: typeof fetch;
}

export async function apiRequest<T extends z.ZodType>(
  path: string,
  schema: T,
  options?: RequestOptions,
): Promise<z.infer<T>>;
export async function apiRequest(
  path: string,
  schema: null,
  options?: RequestOptions,
): Promise<void>;
export async function apiRequest<T extends z.ZodType>(
  path: string,
  schema: T | null,
  { method = 'GET', body, fetcher = fetch }: RequestOptions = {},
): Promise<z.infer<T> | void> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers[CSRF_HEADER] = await ensureCsrf(fetcher);
  const response = await fetcher(`/api/v1${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));
    const retry = Number(response.headers.get('retry-after'));
    throw new ApiRequestError(
      response.status,
      parsed.success ? parsed.data.error.code : 'internal_error',
      parsed.success
        ? parsed.data.error.message
        : 'No hemos podido conectar con AILAB. Comprueba tu conexión y vuelve a intentarlo.',
      Number.isFinite(retry) && retry > 0 ? retry : undefined,
    );
  }
  if (schema === null || response.status === 204) return;
  return schema.parse(await response.json());
}

export function errorMessage(error: unknown): string {
  return error instanceof ApiRequestError
    ? error.message
    : 'Ha ocurrido un error inesperado. Recarga la página y vuelve a intentarlo.';
}
